/**
 * OptimizedImage — Progressive image loader
 *
 * Features:
 * - Simple <img> wrapper with error handling
 * - Background image variant for hero sections
 * - Progressive JPEG supported natively by the browser
 *
 * Ported from App-WebTV/src/components/common/OptimizedImage.jsx
 * Adapted for FrontendClient (Next.js) — no TV-specific dependencies.
 */
"use client";

import React, { useState, useRef, useEffect } from "react";

// ============================================================================
// OptimizedImage — Standard <img> replacement
// ============================================================================

const OptimizedImage = ({
    src,
    alt = "",
    className = "",
    style = {},
    onLoad,
    onError,
    ...rest
}) => {
    const [hasError, setHasError] = useState(false);
    const imgRef = useRef(null);

    const handleLoad = (e) => {
        if (onLoad) onLoad(e);
    };

    const handleError = (e) => {
        setHasError(true);
        if (onError) onError(e);
    };

    if (!src || hasError) return null;

    return (
        <div
            style={{ position: "relative", overflow: "hidden", ...style }}
            className={className}
        >
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "left"
                }}
                onLoad={handleLoad}
                onError={handleError}
                loading="lazy"
                {...rest}
            />
        </div>
    );
};

// ============================================================================
// OptimizedBackgroundImage — For hero/banner sections with children overlay
// ============================================================================

export const OptimizedBackgroundImage = ({
    src,
    children,
    className = "",
    style = {},
    ...rest
}) => {
    if (!src) return null;

    return (
        <div
            style={{ position: "relative", overflow: "hidden", ...style }}
            className={className}
            {...rest}
        >
            {/* Main background layer */}
            <div
                suppressHydrationWarning
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundImage: `url(${src})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    zIndex: 2,
                    pointerEvents: "none",
                }}
            />

            {/* Content layer */}
            <div
                style={{
                    position: "relative",
                    zIndex: 3,
                    width: "100%",
                    height: "100%",
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default OptimizedImage;
